import { useState } from "react";
import { Form, Row, Col, message } from "antd";
import { ReusableModal } from "@/components/UI/Modals/CustomModals";
import TextField from "@/components/UI/FormFields/TextField";
import EmailField from "@/components/UI/FormFields/EmailField";
import PhoneField from "@/components/UI/FormFields/PhoneField";
import DateField from "@/components/UI/FormFields/DateField";
import SelectField from "@/components/UI/FormFields/SelectField";
import GooglePlacesAutocomplete from "@/components/forms/GooglePlacesAutocomplete";
import { useAddCustomerMutation } from "@/services/api/user/customerApi";
import { useGetStatesQuery } from "@/services/api/states/stateApi";
import { useGetLGAsQuery } from "@/services/api/lgas/lgaApi";

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

const CreateCustomerModal = ({ open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [selectedStateId, setSelectedStateId] = useState(null);

  const { data: statesResponse, isLoading: isStatesLoading } =
    useGetStatesQuery(undefined, { skip: !open });
  const states = Array.isArray(statesResponse) ? statesResponse : [];

  const { data: lgasResponse, isLoading: isLgasLoading } = useGetLGAsQuery(
    selectedStateId,
    { skip: !selectedStateId },
  );
  const lgas = Array.isArray(lgasResponse) ? lgasResponse : [];

  const [addCustomer, { isLoading: isCreating }] = useAddCustomerMutation();

  const stateOptions = states.map((s) => ({ value: s.id, label: s.name }));
  const lgaOptions = lgas.map((l) => ({ value: l.id, label: l.name }));

  const handleStateChange = (stateId) => {
    setSelectedStateId(stateId);
    form.setFieldValue("lga", undefined);
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedStateId(null);
    onClose();
  };

  const handleSubmit = async (values) => {
    const stateName = states.find((s) => s.id === values.state)?.name;
    const lgaName = lgas.find((l) => l.id === values.lga)?.name;

    try {
      await addCustomer({
        ...values,
        date_of_birth: values.date_of_birth?.format("YYYY-MM-DD"),
        state: stateName,
        lga: lgaName,
        country: "Nigeria",
      }).unwrap();

      message.success("Customer created successfully");
      const fullName = `${values.first_name} ${values.last_name}`.trim();
      form.resetFields();
      setSelectedStateId(null);
      onSuccess?.(fullName);
      onClose();
    } catch (error) {
      message.error(error?.data?.message || "Failed to create customer");
    }
  };

  return (
    <ReusableModal
      open={open}
      onClose={handleClose}
      title="Create Customer"
      subtitle="Add a new customer to the system"
      width={720}
      steps={[{ title: "Details" }]}
      showSteps={false}
      showTabs={false}
      form={form}
      onFormFinish={handleSubmit}
      isLoading={isCreating}
      finishText="Create Customer"
    >
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <TextField name="first_name" label="First Name" required />
          </Col>
          <Col xs={24} md={8}>
            <TextField name="middle_name" label="Middle Name" />
          </Col>
          <Col xs={24} md={8}>
            <TextField name="last_name" label="Last Name" required />
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <EmailField />
          </Col>
          <Col xs={24} md={8}>
            <PhoneField name="phone" />
          </Col>
          <Col xs={24} md={8}>
            <PhoneField name="whatsapp_phone" label="WhatsApp Number" />
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <DateField
              name="date_of_birth"
              label="Date of Birth"
              required
            />
          </Col>
          <Col xs={24} md={12}>
            <SelectField
              name="gender"
              label="Gender"
              options={GENDER_OPTIONS}
              required
            />
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <SelectField
              name="state"
              label="State"
              options={stateOptions}
              loading={isStatesLoading}
              showSearch
              onSelect={handleStateChange}
              required
            />
          </Col>
          <Col xs={24} md={12}>
            <SelectField
              name="lga"
              label="LGA"
              options={lgaOptions}
              loading={isLgasLoading}
              showSearch
              readOnly={!selectedStateId}
              required
            />
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              name="address"
              label="Address"
              rules={[{ required: true, message: "Please enter address" }]}
            >
              <GooglePlacesAutocomplete
                placeholder="Enter address..."
                className="p-3 rounded-lg text-base"
                onPlaceSelect={(place) =>
                  form.setFieldValue("address", place.address)
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </ReusableModal>
  );
};

export default CreateCustomerModal;
